import { useCallback } from "react";

const buildLocalDateTime = (eventDate: Date | string, timeValue: string) => {
  const baseDate = new Date(eventDate);
  if (Number.isNaN(baseDate.getTime())) return null;

  const [hoursStr, minutesStr, secondsStr = "0"] = timeValue.split(":");
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  const seconds = Number(secondsStr);

  if ([hours, minutes, seconds].some((value) => Number.isNaN(value))) return null;

  return new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate(),
    hours,
    minutes,
    seconds,
    0,
  );
};

type TimeValidationOptions = {
  minimumDurationMinutes?: number;
  validateEndNotPast?: boolean;
  validatePastStartTime?: boolean;
};

export function useValidateTimeFields() {
  const validateTimeFields = useCallback(
    (
      start: string,
      end: string,
      eventDate: Date | string | null | undefined,
      {
        minimumDurationMinutes = 15,
        validateEndNotPast = false,
        validatePastStartTime = false,
      }: TimeValidationOptions = {},
    ): string | null => {
      if (!start || !end || !eventDate) return null;

      const startTime = buildLocalDateTime(eventDate, start);
      const endTime = buildLocalDateTime(eventDate, end);
      if (!startTime || !endTime) return null;

      const timeDiff = endTime.getTime() - startTime.getTime();
      const minDiffMs = minimumDurationMinutes * 60 * 1000;

      if (minimumDurationMinutes === 0 && timeDiff <= 0) {
        return "End time must be after start time.";
      }

      if (minimumDurationMinutes > 0 && timeDiff < minDiffMs) {
        return "End time must be at least 15 minutes after start time.";
      }

      const selectedDate = new Date(eventDate);
      if (Number.isNaN(selectedDate.getTime())) return null;

      if (validateEndNotPast && endTime < new Date()) {
        return "End date and time cannot be in the past.";
      }

      if (!validatePastStartTime) return null;

      const today = new Date();
      const isToday =
        selectedDate.getFullYear() === today.getFullYear() &&
        selectedDate.getMonth() === today.getMonth() &&
        selectedDate.getDate() === today.getDate();

      if (isToday) {
        const now = new Date();
        now.setSeconds(0, 0);

        if (startTime < now) {
          return "Start time cannot be in the past for today's events.";
        }
      }

      return null;
    },
    [],
  );

  return { validateTimeFields };
}
